
import type { User } from '@empathyco/playboard-core-domain'
import type { PropType } from 'vue'
import type { ErrorMessage, UserRoleManagementMessages } from '../../models'
import { defineComponent, nextTick, ref } from 'vue'
import { useAppLoading } from '../../composables/use-app-loading'
import { useUsersError } from '../../composables/users/use-users-error'
import { UiModal } from '../keen-ui'
import EpbAccordion from '../shared/epb-accordion.component.vue'
import { EpbButton } from '../shared/epb-button'
import { EpbTextbox } from '../shared/epb-textbox'
import RoleAssignments from './role-assignments.component.vue'

type Error = Omit<ErrorMessage, 'message'> & ({ message: string } | { messages: Array<string> })

export default defineComponent({
  name: 'UserEditModal',
  components: {
    EpbAccordion,
    EpbButton,
    EpbTextbox,
    RoleAssignments,
    UiModal,
  },
  props: {
    user: {
      type: Object as PropType<User>,
      default: undefined,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    messages: {
      type: Object as PropType<UserRoleManagementMessages['edit']>,
      required: true,
    },
  },
  emits: {
    success: (message: string) => {},
    error: (message: Error) => {},
  },
  setup(props, { emit }) {
    const { setLoading } = useAppLoading()
    const { getErrorMessage } = useUsersError()

    const baseClass = 'epb-user-edit-modal'

    const sections = Object.entries(props.messages.sections).map(([id, { title }]) => ({
      id,
      title,
    }))

    const modal = ref()

    function open(): void {
      nextTick(() => modal.value.open())
    }

    function close(): void {
      modal.value.close()
    }

    async function save(): Promise<void> {
      setLoading(true)
      try {
        await props.user.save()
        close()
        emit('success', props.messages.success)
      } catch (errors) {
        let error: Error
        if (errors.length === 1) {
          error = getErrorMessage(errors[0])
        } else {
          error = {
            title: props.messages.error,
            messages: errors.map(err => {
              const { title, message } = getErrorMessage(err.payload)
              return `<b>${title} (${err.item.role.name})</b><br>${message}`
            }),
          }
        }
        emit('error', error)
      } finally {
        setLoading(false)
      }
    }

    return {
      baseClass,
      sections,
      modal,
      open,
      close,
      save,
    }
  },
})
